import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";
import EditModal from "@/components/site/EditModal";
import { SafeHTML } from "@/components/site/RichTextEditor";

const PAGE_SLUGS = [
  { value: "home", label: "🏠 Accueil" },
  { value: "actualites", label: "📰 Actualités" },
  { value: "comprendre", label: "📚 Comprendre" },
  { value: "agir", label: "✊ Agir" },
  { value: "actions", label: "🎬 Actions (agenda)" },
  { value: "carte", label: "🗺️ Carte" },
  { value: "avis", label: "🗳️ Donner mon avis" },
  { value: "temoignages", label: "🎙️ Témoignages" },
  { value: "cef", label: "🏛️ Le CEF" },
  { value: "rejoindre", label: "👥 Rejoindre" },
  { value: "profil", label: "👤 Espace membre (Profil)" },
  { value: "login", label: "🔐 Connexion" },
  { value: "signup", label: "✏️ Inscription" },
];

const FIELDS = [
  { key: "title", label: "Titre du bloc (optionnel)", span: 2 },
  { key: "section_key", label: "Clé de section", required: true, hint: "Identifiant technique du bloc. Pour remplacer un texte existant du site (ex: l'intro 'Groupes d'élèves locaux' sur /carte), utilise la clé exacte affichée sur la page (ex: groupes-intro). Pour ajouter un nouveau bloc libre, choisis ton propre nom (ex: intro, outro, encart-1)." },
  { key: "order", label: "Ordre", type: "number" },
  { key: "html", label: "Contenu — mise en forme complète", type: "richtext", required: true },
  { key: "active", label: "Actif (visible sur le site)", type: "checkbox" },
  { key: "members_only", label: "Réservé aux membres connecté·es", type: "checkbox", hint: "Si coché, ce bloc ne sera visible que pour les utilisateurs connectés." },
];

// Pages with known editable-text zones (section_key that overrides hardcoded text on the public site).
// Keep this in sync with the EditableText components scattered in the codebase.
const EDITABLE_TEXT_KEYS = {
  home: [
    { key: "bottom-cta", label: "Bannière noire (sous-titre)" },
  ],
  carte: [
    { key: "groupes-intro", label: "Intro section 'Groupes d'élèves locaux'" },
  ],
  cef: [
    { key: "bottom-cta", label: "Bannière noire (sous-titre)" },
  ],
  rejoindre: [
    { key: "signup-cta-body", label: "Encart rose — 'Rejoindre le mouvement'" },
    { key: "cef-cta-body", label: "Encart jaune — 'Devenir membre officiel du CEF'" },
    { key: "contact-body", label: "Carte 'Contact direct'" },
    { key: "participate-body", label: "Carte 'Participe maintenant'" },
  ],
  login: [
    { key: "subtitle", label: "Sous-titre de la page Connexion" },
  ],
  signup: [
    { key: "subtitle", label: "Sous-titre de la page Inscription" },
  ],
};

export default function PageBlocksTab() {
  const [pageSlug, setPageSlug] = React.useState("home");
  const [blocks, setBlocks] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(null);

  const load = React.useCallback(() => {
    api.get("/admin/page-blocks", { params: { page_slug: pageSlug } })
      .then((r) => setBlocks(r.data))
      .catch(() => {});
  }, [pageSlug]);
  React.useEffect(() => { load(); }, [load]);

  const save = async (vals) => {
    try {
      await api.patch(`/admin/page-blocks/${editing.id}`, vals);
      toast.success("Bloc mis à jour");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); throw err; }
  };

  const create = async (vals) => {
    try {
      await api.post("/admin/page-blocks", { ...vals, page_slug: pageSlug });
      toast.success("Bloc créé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); throw err; }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce bloc ?")) return;
    await api.delete(`/admin/page-blocks/${id}`);
    toast.success("Supprimé"); load();
  };

  const toggleActive = async (b) => {
    await api.patch(`/admin/page-blocks/${b.id}`, { active: !b.active });
    load();
  };

  return (
    <div data-testid="page-blocks-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Zones de texte des pages</h2>
          <p className="text-sm text-neutral-600 mt-1">Ajoute du contenu riche (titres, <strong>gras</strong>, <em>italique</em>, liens, listes, images, vidéos…) sur n'importe quelle page publique. Choisis la page dans le menu déroulant à droite, puis « <em>Nouveau bloc</em> ». Tu peux mettre plusieurs blocs par page, les ordonner, les désactiver temporairement, ou les réserver aux membres connecté·es.</p>
          <p className="text-xs text-neutral-500 mt-2">💡 <strong>Astuce :</strong> certaines zones de texte déjà visibles sur le site peuvent être <strong>remplacées</strong> en créant un bloc avec la même clé de section (voir la liste « Zones remplaçables » ci-dessous).</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="neo-input text-sm py-2" value={pageSlug} onChange={(e) => setPageSlug(e.target.value)} data-testid="page-slug-select">
            {PAGE_SLUGS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button onClick={() => setCreating(true)} className="neo-btn bg-cef-blue text-white text-sm py-2" data-testid="new-page-block-btn"><Plus className="w-4 h-4" /> Nouveau bloc</button>
        </div>
      </div>

      {EDITABLE_TEXT_KEYS[pageSlug] && EDITABLE_TEXT_KEYS[pageSlug].length > 0 && (
        <div className="neo-card p-4 mb-4 bg-cef-yellow/30 border-cef-yellow" data-testid="editable-keys-hint">
          <div className="font-bold text-sm uppercase tracking-widest mb-2">Zones de texte remplaçables sur cette page</div>
          <ul className="grid gap-1 text-sm">
            {EDITABLE_TEXT_KEYS[pageSlug].map((k) => {
              const exists = blocks.some((b) => b.section_key === k.key);
              return (
                <li key={k.key} className="flex items-center justify-between gap-2 flex-wrap">
                  <span><code className="bg-white px-1.5 py-0.5 border border-black/20 rounded text-xs font-bold">{k.key}</code> — {k.label}</span>
                  {exists ? (
                    <span className="chip bg-cef-green text-xs">✓ déjà remplacée</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setCreating({ section_key: k.key, order: blocks.length, html: "", active: true, title: k.label })}
                      className="neo-btn bg-cef-blue text-white text-xs py-1 px-2"
                      data-testid={`prefill-${k.key}`}
                    >
                      + Remplacer
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="grid gap-3" data-testid="page-blocks-list">
        {blocks.length === 0 && <div className="neo-card p-5 bg-white text-neutral-500">Aucun bloc sur cette page. Crée le premier !</div>}
        {blocks.map((b) => (
          <div key={b.id} className={`neo-card p-4 ${!b.active ? "opacity-60" : ""}`} data-testid={`page-block-admin-${b.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  {!b.active && <span className="chip bg-neutral-300 text-xs">💤 inactif</span>}
                  {b.members_only && <span className="chip bg-cef-pink text-white text-xs" data-testid={`block-members-${b.id}`}>👥 membres</span>}
                  <span className="chip bg-cef-yellow text-xs">#{b.section_key}</span>
                  <span className="chip bg-white text-xs">ordre: {b.order}</span>
                  {b.title && <span className="font-bold">{b.title}</span>}
                </div>
                <SafeHTML html={b.html} className="mt-2 text-sm text-neutral-700 prose-sm line-clamp-3" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(b)} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`edit-block-${b.id}`}><Edit3 className="w-4 h-4" /> Éditer</button>
                <button onClick={() => toggleActive(b)} className={`neo-btn text-sm py-2 ${b.active ? "bg-white" : "bg-cef-green"}`} data-testid={`toggle-block-${b.id}`}>
                  {b.active ? "Désactiver" : "Activer"}
                </button>
                <button onClick={() => del(b.id)} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid={`del-block-${b.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <EditModal title={`Éditer le bloc · ${pageSlug}`} initial={editing} fields={FIELDS} onClose={() => setEditing(null)} onSave={save} />}
      {creating && <EditModal title={`Nouveau bloc · ${pageSlug}`} initial={creating} fields={FIELDS} onClose={() => setCreating(null)} onSave={(v) => { setCreating(null); return create(v); }} />}
    </div>
  );
}
